import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Получает список шаблонов задач с применением фильтров.
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные шаблонов задач.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getList = async (params, isAdmin = false) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/task-templates${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает список всех шаблонов задач без пагинации
 * @param {Object} params - Параметры фильтрации.
 * @returns {Promise<Object>} - Данные шаблонов задач.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getTemplatesOptions = async (params, isAdmin = false) => {
  const cleanedFilters = cleanParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  });
  const queryString = new URLSearchParams(cleanedFilters).toString();

  try {
    const response = await fetch(
      `/api/task-templates${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает шаблон задачи по UUID.
 * @param {string} uuid - UUID шаблона задачи.
 * @returns {Promise<Object>} - Данные шаблона задачи.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getItem = async (uuid, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/task-template${isAdmin ? "/admin" : ""}?uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.template;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новый шаблон задачи.
 * @param {Object} item - Данные шаблона задачи.
 * @returns {Promise<Object>} - Созданный шаблон задачи.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/task-template", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.template;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновляет существующий шаблон задачи.
 * @param {Object} item - Данные шаблона задачи.
 * @returns {Promise<Object>} - Обновленный шаблон задачи.
 * @throws {Error} - Ошибка при обновлении данных.
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/task-template", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.template;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаляет шаблон задачи.
 * @param {Object} item - Данные шаблона задачи.
 * @returns {Promise<Object>} - Удаленный шаблон задачи.
 * @throws {Error} - Ошибка при удалении данных.
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/task-template", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.template;
  } catch (err) {
    throw err;
  }
};

/**
 * Прикрепить файл к шаблону задачи
 * @param {Object} item - Данные файла.
 * @returns {Promise<Object>} - Созданный файл.
 * @throws {Error} - Ошибка при создании данных.
 */
export const addFile = async (item) => {
  try {
    const response = await fetch("/api/task-template/file", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Прикрепить ярлык к шаблону задачи
 * @param {Object} item - Данные ярлыка.
 * @returns {Promise<Object>} - Созданный ярлык.
 * @throws {Error} - Ошибка при создании данных.
 */
export const addFileLink = async (item) => {
  try {
    const response = await fetch("/api/task-template/file/shortcut", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить файлы шаблона задачи
 * @param {Object} params - { template_uuid } template_uuid - id шаблона задачи
 * @returns {Promise<Object>} - Содержит файлы шаблона задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getTaskTemplateFiles = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(`/api/task-template/files?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удалить файл шаблона задачи
 * @param {Object} item - Данные удаляемого файла
 * @returns {Promise<Object>} - Содержит подтверждение удаления
 * @throws {Error} - Ошибка при получении данных.
 */
export const deleteTaskTemplateFile = async (item) => {
  try {
    const response = await fetch(`/api/task-template/file`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить связанные ресурсы шаблона задачи
 * @param {Object} params - { template_uuid } template_uuid - id шаблона задачи
 * @returns {Promise<Object>} - Содержит связанные ресурсы шаблона задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getTaskTemplateObjects = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(`/api/task-template/objects?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.attachedObjects || [];
  } catch (err) {
    throw err;
  }
};

/**
 * Получение роли пользователя в шаблоне задачи
 * @param {string} uuid - UUID шаблона задачи
 * @returns {Promise<Object>} - Роль пользователя в шаблоне задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getUserRoles = async (uuid) => {
  try {
    const response = await fetch(`/api/task-template/roles?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.roles;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка объектов, которые можно прикрепить к шаблону задачи
 * @param {Object} params - {template_uuid, definition_uuid} template_uuid - id шаблона задачи, definition_uuid - id описателя
 * @returns {Promise<Object>} - Содержит список объектов, которые можно прикрепить к шаблону задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getAttachableObjects = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/task-template/attachable-objects${
        isAdmin ? "/admin" : ""
      }?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.attachableObjects;
  } catch (err) {
    throw err;
  }
};

/**
 * Прикрепление объектов к шаблону задачи
 * @param {Object} params - {template_uuid, resources} template_uuid - id шаблона задачи, resources - массив объектов
 * @returns {Promise<Object>} - Содержит список прикрепленных объектов
 * @throws {Error} - Ошибка при прикреплении объектов.
 */
export const attachObjects = async (params, isAdmin = false) => {
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `/api/task-template/objects${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при прикреплении объектов. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление объекта из шаблона задачи
 * @param {Object} params - {template_uuid, object_uuid} template_uuid - id шаблона задачи, object_uuid - id объекта
 * @returns {Promise<Object>} - Содержит список прикрепленных объектов
 * @throws {Error} - Ошибка при удалении объекта.
 */
export const deleteAttachedObject = async (params, isAdmin = false) => {
  try {
    const response = await fetch(
      `/api/task-template/object${isAdmin ? "/admin" : ""}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении объекта. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение чеклиста шаблона задачи
 * @param {string} uuid - UUID шаблона задачи
 * @returns {Promise<Object>} - Чеклист шаблона задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getChecklist = async (uuid) => {
  try {
    const response = await fetch(
      `/api/task-template/checklist?template_uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.checklist;
  } catch (err) {
    throw err;
  }
};

/**
 * Добавление элемента в чеклист шаблона задачи
 * @param {Object} item - Данные элемента чеклиста {template_uuid, title, idx}
 * @returns {Promise<Object>} - Добавленный элемент чеклиста
 * @throws {Error} - Ошибка при добавлении элемента.
 */
export const addChecklistItem = async (item) => {
  try {
    const response = await fetch(`/api/task-template/checklist/item`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении элемента в чек-лист. ${
          data.error || "Попробуйте снова"
        }`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление элемента чеклиста шаблона задачи
 * @param {Object} item - Данные элемента чеклиста {uuid, title, idx}
 * @returns {Promise<Object>} - Обновленный элемент чеклиста
 * @throws {Error} - Ошибка при обновлении элемента.
 */
export const updateChecklistItem = async (item) => {
  try {
    const response = await fetch(`/api/task-template/checklist/item`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении элемента чек-листа. ${
          data.error || "Попробуйте снова"
        }`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление элемента из чеклиста шаблона задачи
 * @param {Object} item - UUID элемента чеклиста
 * @returns {Promise<Object>} - Удаленный элемент чеклиста
 */
export const deleteChecklistItem = async (item) => {
  try {
    const response = await fetch(`/api/task-template/checklist/item`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении элемента чек-листа. ${
          data.error || "Попробуйте снова"
        }`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление чеклиста шаблона задачи
 * @param {Object} item - Данные шаблона задачи {template_uuid}
 * @returns {Promise<Object>} - Удаленный чеклист
 */
export const deleteChecklist = async (template_uuid) => {
  try {
    const response = await fetch(`/api/task-template/checklist`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ template_uuid }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении чек-листа. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение правил повторения шаблона задачи
 * @param {string} uuid - UUID шаблона задачи
 * @returns {Promise<Object>} - Правила повторения шаблона задачи
 * @throws {Error} - Ошибка при получении данных.
 */
export const getRepeatRules = async (uuid) => {
  try {
    const response = await fetch(`/api/task-template/recurrence?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении правил повторения задачи. ${
          data.error || "Попробуйте снова"
        }`
      );
    }

    return data.rules;
  } catch (err) {
    throw err;
  }
};
